import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { invoiceQueryKeys } from "../api/queryKeys";

import { instance } from "@/lib/axios/instance";
import { CampaignIntegrationMonthType } from "@/types/response/CampaignIntegrationMonthType";
import { PagedResponse } from "@/types/response/PagedResponse";

type UseIntegrationModuleCampaignsParams = {
  descending?: boolean;
  tenantIntegrationModuleId?: string;
  isInvoiced?: boolean;
  month?: string;
  orderBy?: string;
  page?: number;
  search?: string;
  pageSize?: number;
  year?: string;
  tenantId?: string | null;
};

/**
 * Fetches campaigns for a specific tenant integration module, year, and month.
 * Uses GET /api/v2/Integrations/{tenantIntegrationModuleId}/Campaigns/{year}/{month}.
 */
export function useIntegrationModuleCampaigns(
  params: UseIntegrationModuleCampaignsParams
) {
  const {
    descending,
    tenantIntegrationModuleId,
    isInvoiced = false,
    month,
    orderBy,
    page = 1,
    search,
    pageSize = 10,
    year,
    tenantId,
  } = params;

  return useQuery<
    PagedResponse<CampaignIntegrationMonthType>,
    AxiosError
  >({
    queryKey: [
      ...invoiceQueryKeys.cache.integrationModuleCampaigns.byIntegrationModuleYearMonth(
        tenantIntegrationModuleId || "",
        year || "",
        month || ""
      ),
      descending,
      orderBy,
      page,
      search,
      pageSize,
      tenantId,
      isInvoiced,
    ],
    queryFn: () =>
      instance
        .get<PagedResponse<CampaignIntegrationMonthType>>(
          `/api/v2/Integrations/${tenantIntegrationModuleId}/Campaigns/${year}/${month}`,
          {
            params: {
              descending,
              isInvoiced,
              orderBy,
              page,
              search,
              pageSize,
              tenantId: tenantId || undefined,
            },
          }
        )
        .then((res) => res.data),
    placeholderData: keepPreviousData,
    refetchOnReconnect: false,
    refetchOnMount: true,
    staleTime: 20 * 60 * 1000,
    enabled: !!tenantIntegrationModuleId && !!year && !!month,
  });
}
